import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';
import moment from 'moment';

/**
 * Badge priority map
 * Lower number = higher priority
 */
const BADGE_PRIORITY = {
  Platinum: 1,
  Premium: 2,
  'Verified Professional': 3,
};

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  /**
   * 🔥 SORT LISTINGS
   * 1. Badge priority (Platinum → Premium → Verified)
   * 2. Older planCreartedAt first within same badge
   */
  const sortedListings = [...listings].sort((a, b) => {
    const today = moment().startOf('day');

    const getPD = l => l?.attributes?.publicData || {};
    const getAuthorPD = l => l?.author?.attributes?.profile?.publicData || {};

    const aPD = getPD(a);
    const bPD = getPD(b);

    /* ------------------------------------
     * 1️⃣ BOOSTED TODAY (ABSOLUTE PRIORITY)
     * ------------------------------------ */
    const isBoostedToday = pd =>
      Array.isArray(pd.boostedDates) &&
      pd.boostedDates.some(d =>
        moment(d, 'YYYY-MM-DD').isSame(today, 'day')
      );

    const aBoostToday = isBoostedToday(aPD);
    const bBoostToday = isBoostedToday(bPD);

    // One boosted today → always first
    if (aBoostToday !== bBoostToday) {
      return aBoostToday ? -1 : 1;
    }

    // Both boosted today → earlier boostedAt wins
    if (aBoostToday && bBoostToday) {
      const aBoostedAt = moment(aPD.boostedAt).valueOf() || Infinity;
      const bBoostedAt = moment(bPD.boostedAt).valueOf() || Infinity;
      return aBoostedAt - bBoostedAt;
    }

    /* ------------------------------------
     * 2️⃣ BADGE PRIORITY
     * ------------------------------------ */
    const BADGE_PRIORITY = {
      Platinum: 1,
      Premium: 2,
      'Verified Professional': 3,
    };

    const aBadge =
      BADGE_PRIORITY[getAuthorPD(a).subscriptionBadge] ?? 999;
    const bBadge =
      BADGE_PRIORITY[getAuthorPD(b).subscriptionBadge] ?? 999;

    if (aBadge !== bBadge) {
      return aBadge - bBadge;
    }

    /* ------------------------------------
     * 3️⃣ PLAN CREATED (OLDER FIRST)
     * ------------------------------------ */
    const aPlan = moment(
      getAuthorPD(a).planCreartedAt
    ).valueOf() || Infinity;

    const bPlan = moment(
      getAuthorPD(b).planCreartedAt
    ).valueOf() || Infinity;

    return aPlan - bPlan;
  });

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({
          id: 'SearchResultsPanel.screenreader.pagination',
        })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {sortedListings.map(l => (
          <ListingCard
            className={css.listingCard}
            key={l.id.uuid}
            listing={l}
            renderSizes={cardRenderSizes(isMapVariant)}
            setActiveListing={setActiveListing}
          />
        ))}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
